import { gql } from '@apollo/client/core';

// AI Assistant school flags + settings mutation (AI assistant epic, section E).
//
// The backend (sms-be feat/ai-assistant-gemini-mcp) exposes the two per-school
// flags on the existing school/config GraphQL surface with EXACTLY these
// camelCase field names (achievable D1): `aiAssistantEnabled`, `aiStudentAccess`.
//
// Hand-written gql over manually-typed results (same pattern as
// src/graphql/announcements.ts / useAnnouncements — see #223): codegen targets
// the MAIN checkout, so we do NOT regenerate composition-functions.ts from a
// worktree.
// TODO(codegen): once the BE branch is merged and codegen is re-run on the main
// checkout, replace these manual types with the generated School fields.

/** The two per-school AI assistant flags (D1). */
export interface AiAssistantFlags {
  aiAssistantEnabled: boolean;
  aiStudentAccess: boolean;
}

/** Result of AI_ASSISTANT_SCHOOL_FLAGS (admin/staff surface). */
export interface AiAssistantSchoolFlagsResult {
  getSchool: ({ id: string } & AiAssistantFlags) | null;
}

/** Result of AI_ASSISTANT_STUDENT_FLAGS (student surface, via me.school). */
export interface AiAssistantStudentFlagsResult {
  me:
    | {
        __typename: string;
        id?: string;
        school?: ({ id: string } & AiAssistantFlags) | null;
      }
    | null;
}

/**
 * Admin/staff read: the flags live on the school/config surface (getSchool).
 * Kept as a dedicated query document (instead of widening GET_SCHOOL_QUERY) so
 * a backend that hasn't shipped the fields yet only breaks the AI surface,
 * never the whole dashboard shell.
 */
export const AI_ASSISTANT_SCHOOL_FLAGS = gql`
  query AiAssistantSchoolFlags {
    getSchool {
      id
      aiAssistantEnabled
      aiStudentAccess
    }
  }
`;

/**
 * Student read: students cannot call getSchool — their school hangs off `me`.
 */
export const AI_ASSISTANT_STUDENT_FLAGS = gql`
  query AiAssistantStudentFlags {
    me {
      __typename
      ... on Student {
        id
        school {
          id
          aiAssistantEnabled
          aiStudentAccess
        }
      }
    }
  }
`;

/**
 * Admin write (E4/D1). CONTRACT ASSUMPTION: the backend's D1 mutation name is
 * not final yet — `updateSchoolAiSettings` with two Boolean! args is the
 * agreed placeholder and MUST be synced with the BE branch before merge.
 */
export const UPDATE_SCHOOL_AI_SETTINGS = gql`
  mutation UpdateSchoolAiSettings($aiAssistantEnabled: Boolean!, $aiStudentAccess: Boolean!) {
    updateSchoolAiSettings(aiAssistantEnabled: $aiAssistantEnabled, aiStudentAccess: $aiStudentAccess) {
      id
      aiAssistantEnabled
      aiStudentAccess
    }
  }
`;

export interface UpdateSchoolAiSettingsResult {
  updateSchoolAiSettings: ({ id: string } & AiAssistantFlags) | null;
}

export interface UpdateSchoolAiSettingsVariables {
  aiAssistantEnabled: boolean;
  aiStudentAccess: boolean;
}

/**
 * Per-school AI spend/usage snapshot — `School.aiUsage` (sms-be#312).
 *
 * CONTRACT NOTES that every consumer has to honour:
 * - The field is NULLABLE and read-only. It is only populated for school
 *   admins; anyone else gets `null`, never an error. `null` therefore means
 *   "render nothing", not "something went wrong".
 * - Caps are `null` when the school is UNCAPPED. They are NOT 0 — a cap of 0
 *   means "no spend allowed at all", so `null` and `0` must never be collapsed
 *   into one falsy check.
 * - Money is denominated in `currency` and the API performs NO FX conversion,
 *   so amounts must always be formatted with the currency the payload returns
 *   (never a hardcoded ₦ or $).
 * - Whole numbers may arrive as JSON ints (20, not 20.0).
 *
 * TODO(codegen): replace with the generated `SchoolAiUsage` type once sms-be#312
 * is merged and codegen is re-run on the MAIN checkout.
 */
export interface SchoolAiUsage {
  /** ISO 4217 code the amounts below are denominated in, e.g. "USD"/"NGN". */
  currency: string;
  /** `null` == uncapped. */
  monthlyCap: number | null;
  monthlySpend: number;
  /** `null` when uncapped (nothing to count down from). */
  monthlyRemaining: number | null;
  monthlyTokens: number;
  /** `null` == no daily cap; the daily row is omitted entirely in that case. */
  dailyCap: number | null;
  dailySpend: number;
  dailyRemaining: number | null;
  dailyTokens: number;
  periodStart: string;
  periodEnd: string;
  /** Assistant is paused for the period; only the platform can raise the cap. */
  capReached: boolean;
  /** Some usage has no price attached yet, so the money figure understates. */
  hasUnpricedUsage: boolean;
}

export interface SchoolAiUsageResult {
  getSchool: { id: string; aiUsage: SchoolAiUsage | null } | null;
}

/**
 * Admin read of the usage snapshot.
 *
 * Deliberately a SEPARATE document from AI_ASSISTANT_SCHOOL_FLAGS: this branch
 * is stacked on the assistant FE work while `School.aiUsage` ships in its own
 * backend PR, so a backend without the field must fail this query ALONE. Folding
 * `aiUsage` into the flags query would make the whole Settings tab (and the
 * assistant gating) collapse on a server that hasn't deployed sms-be#312 yet.
 */
export const SCHOOL_AI_USAGE = gql`
  query SchoolAiUsage {
    getSchool {
      id
      aiUsage {
        currency
        monthlyCap
        monthlySpend
        monthlyRemaining
        monthlyTokens
        dailyCap
        dailySpend
        dailyRemaining
        dailyTokens
        periodStart
        periodEnd
        capReached
        hasUnpricedUsage
      }
    }
  }
`;
